import BoxKit from "@/shared/components/kit/box";
import CardKit from "@/shared/components/kit/card";
import StackKit from "@/shared/components/kit/stack";
import TypoGraphyKit from "@/shared/components/kit/typoGraphy";
import { useQuery } from "@tanstack/react-query";
import { fetchCreditPurchases } from "@/api/user/home";
import moment from "jalali-moment";
import {
  numberSeparator,
  toPersianNumber,
} from "@/hooks/useThousandsSeparator";
import { useNavigate } from "react-router";
import NorthWestIcon from "@mui/icons-material/NorthWest";
import TableWallet from "../walletReports/table";
import BarChartOutlinedIcon from "@mui/icons-material/BarChartOutlined";
import ContentSkeleton from "../walletReports/content.skeleton";

const CreditPurchases = () => {
  const navigate = useNavigate();

  const { data, isLoading, error } = useQuery({
    queryKey: ["creditPurchases-data"],
    queryFn: () => fetchCreditPurchases(0),
  });

  const dataHeader = [
    { id: "amount", title: "خرید اعتباری" },
    { id: "month", title: "تعداد کل اقساط" },
    { id: "payedCount", title: "اقساط پرداخت شده" },
    { id: "firstInstallmentAmount", title: "مبلغ پرداختی هر قسط" },
    { id: "totalAmount", title: "مبلغ باقیمانده" },
    { id: "clearingAmount", title: "مبلغ تسویه یکجا" },
    { id: "startJalali", title: "تاریخ دریافت اعتبار" },
    { id: "endJalali", title: "تاریخ پایان اعتبار" },
    { id: "details", title: "پذیرنده خرید اعتباری" },
    { id: "moreElement", title: "" },
  ];

  const handleClickDetails = (id: number) => {
    navigate(`/credits/${id}`);
  };

  // Safely process the data - handle both old and new API structures
  let dataNew = null;

  // Check if data exists and get the loans array
  const loansData = data?.data;

  if (loansData) {
    // Handle new structure (data.data.loans)
    if (loansData.loans && Array.isArray(loansData.loans)) {
      dataNew = loansData.loans.map((item: any) => {
        return processLoanItem(item);
      });
    }
    // Handle old structure (data.data is directly an array)
    else if (Array.isArray(loansData)) {
      dataNew = loansData.map((item: any) => {
        return processLoanItem(item);
      });
    }
  }

  // Helper function to process a loan item
  function processLoanItem(item: any) {
    // Add defensive checks for nested properties
    const totalPayed = item.data?.totalPayed || 0;
    const totalAmount = item.data?.totalAmount || 0;
    const totalPaymentSum = totalAmount - totalPayed;

    const payedCount = item.data?.payedCount || item.payedCount || "0";
    const clearingAmount = item.data?.clearingAmount || 0;
    const installments = item.data?.installments || [];

    return {
      ...item,
      amount: `${numberSeparator(item.amount || 0)} ریال`,
      month: toPersianNumber(item.month?.toString() || "0"),
      payedCount: toPersianNumber(payedCount.toString()),
      firstInstallmentAmount:
        installments.length > 0
          ? `${numberSeparator(installments[0].amount || 0)} ریال`
          : "-",
      totalAmount: `${numberSeparator(totalPaymentSum)} ریال`,
      clearingAmount: `${numberSeparator(clearingAmount)} ریال`,
      startJalali: item.start
        ? toPersianNumber(
            moment.unix(item.start).locale("fa").format("jYYYY/jMM/jDD"),
          )
        : "-",
      endJalali: item.end
        ? toPersianNumber(
            moment.unix(item.end).locale("fa").format("jYYYY/jMM/jDD"),
          )
        : "-",
      details: item.data?.details || item.details || "-",
      moreElement: (
        <StackKit direction="row" gap={2}>
          <BoxKit
            onClick={() => handleClickDetails(item.id)}
            sx={{
              boxSizing: "border-box",
              background:
                "linear-gradient(153.86deg, rgba(0, 0, 0, 0.4) -11.84%, rgba(37, 37, 37, 0.370427) 14.24%, rgba(78, 78, 78, 0.340413) 40.7%, rgba(0, 0, 0, 0.237218) 75.66%, rgba(0, 0, 0, 0.12) 106.2%)",
              backdropFilter: "blur(50px)",
              WebkitBackdropFilter: "blur(50px)",
              maxWidth: "100%",
              overflow: "hidden",
              borderRadius: "50%",
              padding: "3px",
              cursor: "pointer",
              width: "30px",
              height: "30px",
            }}
          >
            <NorthWestIcon
              style={{ color: "#fff", width: "18px", height: "" }}
            />
          </BoxKit>
        </StackKit>
      ),
    };
  }

  return (
    <CardKit
      width="100%"
      height={300}
      title={
        <StackKit
          direction="row"
          justifyContent="space-between"
          sx={{ cursor: "pointer" }}
          onClick={() => navigate("/credits/credits-installments")}
        >
          <StackKit direction="row" alignItems="center" gap={1}>
            <BarChartOutlinedIcon style={{ color: "#082A2E" }} />
            <TypoGraphyKit variant="header">خریدهای اعتباری</TypoGraphyKit>
          </StackKit>
        </StackKit>
      }
      sx={{
        background: "#FDFDFD",
        minHeight: "",
        marginBottom: "0 !important",
        paddingBottom: "0 !important",
        overflow: "auto",
      }}
    >
      <StackKit
        sx={{ width: "100%", justifyContent: "center", alignItems: "center" }}
      >
        {isLoading ? (
          <ContentSkeleton />
        ) : error ? (
          <TypoGraphyKit>خطا در بارگذاری داده‌ها</TypoGraphyKit>
        ) : !dataNew || dataNew.length === 0 ? (
          <TypoGraphyKit>داده‌ای یافت نشد</TypoGraphyKit>
        ) : (
          <TableWallet
            dataHeader={dataHeader}
            dataTbody={dataNew}
            search={false}
          />
        )}
      </StackKit>
    </CardKit>
  );
};

export default CreditPurchases;
